import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SearchScreen from './pages/SearchScreen';
import SearchOptions from './pages/SearchOptions';
import ResultsScreen from './pages/ResultsScreen';
import CityComparison from './pages/CityComparison';
import CompareSearch from './pages/CompareSearch';
import CompareResults from './pages/CompareResults';
import { Analytics } from "@vercel/analytics/react"; // Add this import for analytics on Vercel
// Import the new About, Contact, Programs, and Methodology pages
import About from './pages/About';
import Contact from './pages/Contact';
import Programs from './pages/Programs';
import Methodology from './pages/Methodology';
import MapResources from './pages/MapResources';
import Testing from './pages/Testing';
import MediaToolKit from './pages/MediaToolKit';
import NavigationBar from './components/NavigationBar';
import QuickEscape from './components/QuickEscape';
// import Header from './components/Header';
import Footer from './components/Footer';
import './App.css';

// AppContent component that uses useLocation
const AppContent = () => {
  const location = useLocation();
  
  // Pages that use special navigation (SearchScreenNav)
  const specialNavPages = ['/', '/home', '/about', '/methodology', '/contact', '/programs', '/map-resources', '/testing', '/media-tool-kit'];
  const hasSpecialNav = specialNavPages.includes(location.pathname);
  
  // Prevent PWA install prompts
  useEffect(() => {
    const preventInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Don't show the install prompt
      return false;
    };

    window.addEventListener('beforeinstallprompt', preventInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', preventInstallPrompt);
    };
  }, []);
  
  return (
    <div className="app-container">
      {/* Top waves are handled by ::before and ::after pseudo-elements on app-container */}
      
      {/* <Header /> */}
      {/* Only show NavigationBar if NOT on a special nav page */}
      {!hasSpecialNav && <NavigationBar />}
      
      <main className="main-content">
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<SearchScreen />} />
            <Route path="/home" element={<SearchScreen />} />
            <Route path="/search-options" element={<SearchOptions />} />
            <Route path="/results" element={<ResultsScreen />} />
            <Route path="/compare" element={<CityComparison />} />
            <Route path="/compare-search" element={<CompareSearch />} />
            <Route path="/compare-results" element={<CompareResults />} />
            {/* Add the new About, Contact, Programs, and Methodology routes */}
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/map-resources" element={<MapResources />} />
            <Route path="/testing" element={<Testing />} />
            <Route path="/media-tool-kit" element={<MediaToolKit />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:category" element={<ResourceCategory />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </main>
      
      {/* Bottom waves container - positioned above footer */}
      <div className="bottom-waves"></div>
      
      <Footer />
      
      {/* Quick Escape Button - Always visible */}
      <QuickEscape />
    </div>
  );
};

// Main App component that wraps everything in Router
const App = () => {
  return (
    <Router>
      <AppContent />
      <Analytics /> 
    </Router>
  );
};

// Placeholder components for new routes
// In a real app, you would move these to separate files in your 'pages' directory

const ResourcesPage = () => (
  <div className="container mx-auto px-4 py-8">
    <Helmet>
      <title>LGBTQ+ Resources | LGBT PridePath</title>
      <meta name="description" content="Find LGBTQ+ resources including legal rights, healthcare, housing, and employment support. Access over 20,000 community resources across the United States." />
      <link rel="canonical" href="https://www.lgbtpridepath.org/resources" />
    </Helmet>
    <h1 className="text-2xl font-bold mb-6">LGBTQ+ Resources</h1>
    <p className="mb-4">Find resources to support the LGBTQ+ community.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <ResourceCard 
        title="Legal Rights" 
        description="Information about legal protections and rights."
        link="/resources/legal"
      />
      <ResourceCard 
        title="Healthcare" 
        description="Access to affirming healthcare providers and resources."
        link="/resources/health"
      />
      <ResourceCard 
        title="Housing" 
        description="Fair housing resources and anti-discrimination information."
        link="/resources/housing"
      />
      <ResourceCard 
        title="Employment" 
        description="Workplace rights and employment resources."
        link="/resources/employment"
      />
    </div>
  </div>
);

const ResourceCard = ({ title, description, link }) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
    <h2 className="text-xl font-semibold mb-2 text-indigo-700">{title}</h2>
    <p className="text-gray-600 mb-4">{description}</p>
    <a 
      href={link} 
      className="text-indigo-600 font-medium hover:text-indigo-800"
    >
      Learn more →
    </a>
  </div>
);

const ResourceCategory = () => (
  <div className="container mx-auto px-4 py-8">
    <Helmet>
      <title>Resource Category | LGBT PridePath</title>
      <meta name="description" content="Browse specific LGBTQ+ resources by category including legal, healthcare, housing, and employment services." />
      <link rel="canonical" href="https://www.lgbtpridepath.org/resources/category" />
    </Helmet>
    <h1 className="text-2xl font-bold mb-6">Resource Category</h1>
    <p>This would show specific resources for the selected category.</p>
  </div>
);

const TermsPage = () => (
  <div className="container mx-auto px-4 py-8">
    <Helmet>
      <title>Terms of Service | LGBT PridePath</title>
      <meta name="description" content="Read the Terms of Service for LGBT PridePath, a free tool to help LGBTQ+ individuals find communities with legal protections." />
      <link rel="canonical" href="https://www.lgbtpridepath.org/terms" />
    </Helmet>
    <h1 className="text-2xl font-bold mb-6">Terms of Service</h1>
    <p>This page would contain your terms of service content.</p>
  </div>
);

const PrivacyPage = () => (
  <div className="container mx-auto px-4 py-8">
    <Helmet>
      <title>Privacy Policy | LGBT PridePath</title>
      <meta name="description" content="LGBT PridePath privacy policy. We do not collect, store, or share any personally identifiable information from our users." />
      <link rel="canonical" href="https://www.lgbtpridepath.org/privacy" />
    </Helmet>
    <h1 className="text-2xl font-bold mb-6">Privacy Policy</h1>
    <p>This page would contain your privacy policy content.</p>
  </div>
);

const AccessibilityPage = () => (
  <div className="container mx-auto px-4 py-8">
    <Helmet>
      <title>Accessibility Statement | LGBT PridePath</title>
      <meta name="description" content="LGBT PridePath is committed to ensuring digital accessibility for people with disabilities. Learn about our accessibility features and standards." />
      <link rel="canonical" href="https://www.lgbtpridepath.org/accessibility" />
    </Helmet>
    <h1 className="text-2xl font-bold mb-6">Accessibility Statement</h1>
    <p>This page would contain your accessibility statement.</p>
  </div>
);

const NotFoundPage = () => (
  <div className="container mx-auto px-4 py-8 text-center">
    <Helmet>
      <title>Page Not Found | LGBT PridePath</title>
      <meta name="description" content="The page you're looking for doesn't exist. Return to LGBT PridePath to search for LGBTQ+ friendly communities." />
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
    <p className="mb-6">Sorry, the page you're looking for doesn't exist.</p>
    <a 
      href="/" 
      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
    >
      Return to Home
    </a>
  </div>
);

export default App;